import React, { useEffect, useMemo, useCallback } from 'react';
import {
    TextField,
    Grid,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    Divider,
    Autocomplete,
    useTheme,
    alpha,
    LinearProgress,
    Fade,
    Zoom
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { Person, LocationOn, Email, Phone, CreditCard } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';
import { useEnumProductCodeLookupQuery, useGetCitiesQuery, useGetCountiesQuery, useGetNeighborhoodsQuery } from '../../../../../components/services/services';

export interface PhysicalCardFormData {
    userName: string;
    userSurName: string;
    email: string;
    phoneNumber: string;
    cardType: string;
    cityId: number | null;
    cityName: string;
    countyId: number | null;
    countyName: string;
    neighborhoodId: number | null;
    neighborhood: string;
    street: string;
    buildingNo: string;
    companyId: number;
    address?: string;
}

export interface PhysicalCardRequestData {
    embossName1: string;
    embossName2: string;
    cityId: number;
    countyId: number;
    street: string;
    neighborhood: string;
    buildingNo: string;
    productCode: string;
    phoneNumber: string;
    email: string;
    companyId: number;
    address: string;
}

interface CreatePhysicalCardFormProps {
    onFormChange: (formData: PhysicalCardFormData) => void;
    onValidationChange: (isValid: boolean) => void;
    onRequestDataChange?: (requestData: PhysicalCardRequestData | null) => void;
}

const CreatePhysicalCardForm: React.FC<CreatePhysicalCardFormProps> = ({
    onFormChange,
    onValidationChange,
    onRequestDataChange
}) => {
    const theme = useTheme();

    // Centralized field styling
    const fieldStyles = useMemo(() => ({
        '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
                backgroundColor: theme.palette.background.paper,
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
            },
            '&.Mui-focused': {
                backgroundColor: theme.palette.background.paper,
                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                transform: 'translateY(-1px)',
            }
        }
    }), [theme]);
    const { currentUserState } = useSelector((state: RootState) => state?.currentUserState);
    const { control, formState: { errors, isValid, touchedFields }, watch, setValue } = useForm<PhysicalCardFormData>({
        mode: 'onChange',
        defaultValues: {
            userName: '',
            userSurName: '',
            email: '',
            phoneNumber: '',
            cardType: '',
            cityId: null,
            cityName: '',
            countyId: null,
            countyName: '',
            neighborhoodId: null,
            neighborhood: '',
            street: '',
            buildingNo: '',
            companyId: currentUserState?.companyId || 0 // Default company ID
        }
    });
    const { data: enumProductCodeLookUp } = useEnumProductCodeLookupQuery();
    // Watch specific fields for dependencies
    const selectedCityId = watch('cityId');
    const selectedCountyId = watch('countyId');

    // API queries with dependencies - refetch when parent selection changes
    const { data: cities, isLoading: citiesLoading } = useGetCitiesQuery();
    const {
        data: counties,
        isLoading: countiesLoading,
        isFetching: countiesFetching,
        refetch: refetchCounties
    } = useGetCountiesQuery(
        { cityId: selectedCityId || 0 },
        {
            skip: !selectedCityId,
            refetchOnMountOrArgChange: true // Auto refetch when cityId changes
        }
    );
    const {
        data: neighborhoods,
        isLoading: neighborhoodsLoading,
        isFetching: neighborhoodsFetching,
        refetch: refetchNeighborhoods
    } = useGetNeighborhoodsQuery(
        { countyId: selectedCountyId || 0 },
        {
            skip: !selectedCountyId,
            refetchOnMountOrArgChange: true // Auto refetch when countyId changes
        }
    );

    const watchedValues = watch();

    // Transform form data to request format
    const transformToRequestData = useCallback((formData: PhysicalCardFormData): PhysicalCardRequestData | null => {
        if (!formData.userName || !formData.userSurName || !formData.email ||
            !formData.phoneNumber || !formData.cardType || !formData.cityId ||
            !formData.countyId || !formData.neighborhoodId || !formData.neighborhood ||
            !formData.street || !formData.buildingNo || !formData.cityName || !formData.countyName) {
            return null;
        }

        // Clean phone number (remove spaces and formatting)
        const cleanPhoneNumber = formData.phoneNumber.replace(/\D/g, '');

        // Validate phone number format (10 digits, starting with 5)
        if (cleanPhoneNumber.length !== 10 || !cleanPhoneNumber.startsWith('5')) {
            return null;
        }

        const address = `${formData.neighborhood} ${formData.street} ${formData.buildingNo} ${formData.cityName} ${formData.countyName}`;

        return {
            embossName1: formData.userName,
            embossName2: formData.userSurName,
            cityId: formData.cityId,
            countyId: formData.countyId,
            street: formData.street,
            neighborhood: formData.neighborhood,
            buildingNo: formData.buildingNo,
            productCode: formData.cardType,
            phoneNumber: cleanPhoneNumber, // Send clean phone number
            email: formData.email,
            companyId: formData.companyId,
            address: address
        };
    }, []);

    // Reset dependent fields when parent field changes
    React.useEffect(() => {
        if (selectedCityId) {
            // Refetch counties when city changes
            refetchCounties();
        }
        setValue('countyId', null);
        setValue('countyName', '');
        setValue('neighborhoodId', null);
        setValue('neighborhood', '');
    }, [selectedCityId, setValue, refetchCounties]);

    React.useEffect(() => {
        if (selectedCountyId) {
            // Refetch neighborhoods when county changes
            refetchNeighborhoods();
        }
        setValue('neighborhoodId', null);
        setValue('neighborhood', '');
    }, [selectedCountyId, setValue, refetchNeighborhoods]);

    // Watch form changes and pass data to parent
    React.useEffect(() => {
        onFormChange(watchedValues);
        onValidationChange(isValid);

        // Transform and send request data if onRequestDataChange is provided
        if (onRequestDataChange) {
            const requestData = transformToRequestData(watchedValues);
            onRequestDataChange(requestData);
        }
    }, [watchedValues, isValid, onFormChange, onValidationChange, onRequestDataChange]);


    return (
        <Box sx={{ p: 1 }}>
            <Card
                elevation={0}
                sx={{
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.background.paper, 0.6),
                    backdropFilter: 'blur(10px)',
                    overflow: 'hidden'
                }}
            >
                <CardContent sx={{ p: 4 }}>
                    {/* Personal Information Section */}
                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <Person sx={{ mr: 1.5, color: theme.palette.primary.main, fontSize: 28 }} />
                            <Typography variant="h5" sx={{
                                fontWeight: 700,
                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}>
                                Kişisel Bilgiler
                            </Typography>
                        </Box>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="userName"
                                    control={control}
                                    rules={{ required: 'İsim gereklidir' }}
                                    render={({ field }) => {
                                        const isEmpty = !field.value || field.value.trim() === '';
                                        const isTouched = touchedFields.userName;
                                        const showError = !!errors.userName || (isTouched && isEmpty);

                                        return (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="İsim"
                                                placeholder="İsminizi giriniz"
                                                error={showError}
                                                helperText={errors.userName?.message || (isTouched && isEmpty ? 'Bu alan zorunludur' : '')}
                                                variant="outlined"
                                                sx={{
                                                    ...fieldStyles,
                                                    '& .MuiOutlinedInput-root': {
                                                        ...fieldStyles['& .MuiOutlinedInput-root'],
                                                        ...(showError && {
                                                            borderColor: 'error.main',
                                                            '& fieldset': {
                                                                borderColor: 'error.main',
                                                                borderWidth: '2px',
                                                            }
                                                        })
                                                    }
                                                }}
                                            />
                                        );
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="userSurName"
                                    control={control}
                                    rules={{ required: 'Soyisim gereklidir' }}
                                    render={({ field }) => {
                                        const isEmpty = !field.value || field.value.trim() === '';
                                        const isTouched = touchedFields.userSurName;
                                        const showError = !!errors.userSurName || (isTouched && isEmpty);

                                        return (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="Soyisim"
                                                placeholder="Soyisminizi giriniz"
                                                error={showError}
                                                helperText={errors.userSurName?.message || (isTouched && isEmpty ? 'Bu alan zorunludur' : '')}
                                                variant="outlined"
                                                sx={{
                                                    ...fieldStyles,
                                                    '& .MuiOutlinedInput-root': {
                                                        ...fieldStyles['& .MuiOutlinedInput-root'],
                                                        ...(showError && {
                                                            borderColor: 'error.main',
                                                            '& fieldset': {
                                                                borderColor: 'error.main',
                                                                borderWidth: '2px',
                                                            }
                                                        })
                                                    }
                                                }}
                                            />
                                        );
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Controller
                                    name="email"
                                    control={control}
                                    rules={{
                                        required: 'Email gereklidir',
                                        pattern: {
                                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                            message: 'Geçerli bir e-posta adresi giriniz'
                                        }
                                    }}
                                    render={({ field }) => {
                                        const isEmpty = !field.value || field.value.trim() === '';
                                        const isTouched = touchedFields.email;
                                        const showError = !!errors.email || (isTouched && isEmpty);

                                        return (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="Email Adresi"
                                                placeholder="example@email.com"
                                                type="email"
                                                error={showError}
                                                helperText={errors.email?.message || (isTouched && isEmpty ? 'Bu alan zorunludur' : '')}
                                                variant="outlined"
                                                InputProps={{
                                                    startAdornment: (
                                                        <Email sx={{ mr: 1, color: theme.palette.text.secondary }} />
                                                    ),
                                                }}
                                                sx={{
                                                    ...fieldStyles,
                                                    '& .MuiOutlinedInput-root': {
                                                        ...fieldStyles['& .MuiOutlinedInput-root'],
                                                        ...(showError && {
                                                            borderColor: 'error.main',
                                                            '& fieldset': {
                                                                borderColor: 'error.main',
                                                                borderWidth: '2px',
                                                            }
                                                        })
                                                    }
                                                }}
                                            />
                                        );
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="phoneNumber"
                                    control={control}
                                    rules={{
                                        required: 'Telefon numarası gereklidir',
                                        validate: (value) => {
                                            // Remove all non-digit characters
                                            const digitsOnly = value.replace(/\D/g, '');

                                            // Check if exactly 10 digits
                                            if (digitsOnly.length !== 10) {
                                                return 'Telefon numarası 10 haneli olmalıdır';
                                            }

                                            // Check if it's a valid Turkish mobile number (starts with 5)
                                            if (!digitsOnly.startsWith('5')) {
                                                return 'Geçerli bir Türk cep telefonu numarası giriniz (5XX XXX XX XX)';
                                            }

                                            return true;
                                        }
                                    }}
                                    render={({ field }) => {
                                        const isEmpty = !field.value || field.value.trim() === '';
                                        const isTouched = touchedFields.phoneNumber;
                                        const showError = !!errors.phoneNumber || (isTouched && isEmpty);

                                        return (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="Telefon Numarası"
                                                placeholder="5XX XXX XX XX"
                                                error={showError}
                                                helperText={errors.phoneNumber?.message || (isTouched && isEmpty ? 'Bu alan zorunludur' : "10 haneli cep telefonu numarası (5XX XXX XX XX)")}
                                                variant="outlined"
                                                onChange={(e) => {
                                                    // Allow only digits and format automatically
                                                    let value = e.target.value.replace(/\D/g, '');

                                                    // Limit to 10 digits
                                                    if (value.length > 10) {
                                                        value = value.slice(0, 10);
                                                    }

                                                    // Format as 5XX XXX XX XX
                                                    if (value.length > 0) {
                                                        if (value.length <= 3) {
                                                            value = value;
                                                        } else if (value.length <= 6) {
                                                            value = value.slice(0, 3) + ' ' + value.slice(3);
                                                        } else if (value.length <= 8) {
                                                            value = value.slice(0, 3) + ' ' + value.slice(3, 6) + ' ' + value.slice(6);
                                                        } else {
                                                            value = value.slice(0, 3) + ' ' + value.slice(3, 6) + ' ' + value.slice(6, 8) + ' ' + value.slice(8);
                                                        }
                                                    }

                                                    field.onChange(value);
                                                }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <Phone sx={{ mr: 1, color: theme.palette.text.secondary }} />
                                                    ),
                                                }}
                                                sx={{
                                                    ...fieldStyles,
                                                    '& .MuiOutlinedInput-root': {
                                                        ...fieldStyles['& .MuiOutlinedInput-root'],
                                                        ...(showError && {
                                                            borderColor: 'error.main',
                                                            '& fieldset': {
                                                                borderColor: 'error.main',
                                                                borderWidth: '2px',
                                                            }
                                                        })
                                                    }
                                                }}
                                            />
                                        );
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="cardType"
                                    control={control}
                                    rules={{ required: 'Kart tipi seçimi gereklidir' }}
                                    render={({ field }) => {
                                        const isEmpty = !field.value;
                                        const isTouched = touchedFields.cardType;
                                        const showError = !!errors.cardType || (isTouched && isEmpty);

                                        return (
                                            <FormControl fullWidth variant="outlined" error={showError}>
                                                <InputLabel>Kart Tipi</InputLabel>
                                                <Select
                                                    {...field}
                                                    label="Kart Tipi"
                                                    startAdornment={
                                                        <CreditCard sx={{ mr: 1, color: theme.palette.text.secondary }} />
                                                    }
                                                    sx={{
                                                        borderRadius: 2,
                                                        backgroundColor: alpha(theme.palette.background.paper, 0.8),
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.background.paper,
                                                        },
                                                        '&.Mui-focused': {
                                                            backgroundColor: theme.palette.background.paper,
                                                            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                                                        },
                                                        ...(showError && {
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'error.main',
                                                                borderWidth: '2px',
                                                            }
                                                        })
                                                    }}
                                                >
                                                    {enumProductCodeLookUp?.data?.filter((item: any) => item.type === 'P').map((item: any) => (
                                                        <MenuItem key={item.code} value={item.code}>
                                                            {item.title}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {(errors.cardType || (isTouched && isEmpty)) && (
                                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, mx: 1.75 }}>
                                                        {errors.cardType?.message || 'Bu alan zorunludur'}
                                                    </Typography>
                                                )}
                                            </FormControl>
                                        );
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider sx={{ my: 4, borderColor: alpha(theme.palette.primary.main, 0.1) }} />

                    {/* Address Information Section */}
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <LocationOn sx={{ mr: 1.5, color: theme.palette.primary.main, fontSize: 28 }} />
                            <Typography variant="h5" sx={{
                                fontWeight: 700,
                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}>
                                Adres Bilgileri
                            </Typography>
                        </Box>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="cityId"
                                    control={control}
                                    rules={{ required: 'Şehir seçimi gereklidir' }}
                                    render={({ field }) => {
                                        const isEmpty = !field.value;
                                        const isTouched = touchedFields.cityId;
                                        const showError = !!errors.cityId || (isTouched && isEmpty);

                                        return (
                                            <Autocomplete
                                                options={cities?.data || []}
                                                getOptionLabel={(option) => option?.name || ''}
                                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                                value={
                                                    (cities?.data || []).find((city: any) => city?.id === field.value) || null
                                                }
                                                onChange={(_, value) => {
                                                    field.onChange(value ? value.id : null);
                                                    // Also update the city name field for display
                                                    setValue('cityName', value ? value.name ?? '' : '');
                                                }}
                                                loading={citiesLoading}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Şehir"
                                                        placeholder="Şehir seçiniz"
                                                        error={showError}
                                                        helperText={errors.cityId?.message || (isTouched && isEmpty ? 'Bu alan zorunludur' : '')}
                                                        variant="outlined"
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: 2,
                                                                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                                                                '&:hover': {
                                                                    backgroundColor: theme.palette.background.paper,
                                                                },
                                                                '&.Mui-focused': {
                                                                    backgroundColor: theme.palette.background.paper,
                                                                    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                                                                },
                                                                ...(showError && {
                                                                    '& fieldset': {
                                                                        borderColor: 'error.main',
                                                                        borderWidth: '2px',
                                                                    }
                                                                })
                                                            }
                                                        }}
                                                    />
                                                )}
                                                sx={{
                                                    '& .MuiAutocomplete-listbox': {
                                                        maxHeight: 200,
                                                    },
                                                    '& .MuiAutocomplete-option': {
                                                        '&:hover': {
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                        },
                                                    }
                                                }}
                                            />
                                        );
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="countyId"
                                    control={control}
                                    rules={{ required: 'İlçe gereklidir' }}
                                    render={({ field }) => {
                                        const isEmpty = !field.value;
                                        const isTouched = touchedFields.countyId;
                                        const showError = !!errors.countyId || (isTouched && isEmpty);

                                        return (
                                            <Autocomplete
                                                options={counties?.data || []}
                                                getOptionLabel={(option) => option?.name || ''}
                                                isOptionEqualToValue={(option, value) => option?.townCode === value?.townCode}
                                                value={
                                                    (counties?.data || []).find((county: any) => county?.townCode === field.value) || null
                                                }
                                                onChange={(_, value) => {
                                                    field.onChange(value ? value.townCode : null);
                                                    setValue('countyName', value ? value.name ?? '' : '');
                                                }}
                                                disabled={!selectedCityId}
                                                loading={countiesLoading || countiesFetching}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="İlçe"
                                                        placeholder={selectedCityId ? "İlçe seçiniz" : "Önce şehir seçiniz"}
                                                        error={showError}
                                                        helperText={errors.countyId?.message || (isTouched && isEmpty ? 'Bu alan zorunludur' : '')}
                                                        variant="outlined"
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: 2,
                                                                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                                                                '&:hover': {
                                                                    backgroundColor: theme.palette.background.paper,
                                                                },
                                                                '&.Mui-focused': {
                                                                    backgroundColor: theme.palette.background.paper,
                                                                    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                                                                },
                                                                ...(showError && {
                                                                    '& fieldset': {
                                                                        borderColor: 'error.main',
                                                                        borderWidth: '2px',
                                                                    }
                                                                })
                                                            }
                                                        }}
                                                    />
                                                )}
                                                sx={{
                                                    '& .MuiAutocomplete-listbox': {
                                                        maxHeight: 200,
                                                    },
                                                    '& .MuiAutocomplete-option': {
                                                        '&:hover': {
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                        },
                                                    }
                                                }}
                                            />
                                        );
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="neighborhoodId"
                                    control={control}
                                    rules={{ required: 'Mahalle gereklidir' }}
                                    render={({ field }) => (
                                        <Autocomplete
                                            options={neighborhoods?.data || []}
                                            getOptionLabel={(option) => option?.neighborhoodName || ''}
                                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                            value={
                                                (neighborhoods?.data || []).find((neighborhood: any) => neighborhood?.id === field.value) || null
                                            }
                                            onChange={(_, value) => {
                                                field.onChange(value ? value.id : null);
                                                setValue('neighborhood', value ? value.neighborhoodName ?? '' : '');
                                            }}
                                            disabled={!selectedCountyId}
                                            loading={neighborhoodsLoading || neighborhoodsFetching}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Mahalle"
                                                    placeholder={selectedCountyId ? "Mahalle seçiniz" : "Önce ilçe seçiniz"}
                                                    error={!!errors.neighborhoodId}
                                                    helperText={errors.neighborhoodId?.message}
                                                    variant="outlined"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                            backgroundColor: alpha(theme.palette.background.paper, 0.8),
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.background.paper,
                                                            },
                                                            '&.Mui-focused': {
                                                                backgroundColor: theme.palette.background.paper,
                                                                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                                                            }
                                                        }
                                                    }}
                                                />
                                            )}
                                            sx={{
                                                '& .MuiAutocomplete-listbox': {
                                                    maxHeight: 200,
                                                },
                                                '& .MuiAutocomplete-option': {
                                                    '&:hover': {
                                                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                    },
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="street"
                                    control={control}
                                    rules={{
                                        required: 'Sokak gereklidir',
                                        maxLength: {
                                            value: 50,
                                            message: 'Sokak en fazla 50 karakter olabilir'
                                        }
                                    }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Sokak"
                                            placeholder="Sokak giriniz"
                                            error={!!errors.street}
                                            helperText={errors.street?.message}
                                            variant="outlined"
                                            inputProps={{ maxLength: 50 }}
                                            sx={fieldStyles}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="buildingNo"
                                    control={control}
                                    rules={{
                                        required: 'Bina numarası gereklidir',
                                        maxLength: {
                                            value: 50,
                                            message: 'Bina numarası en fazla 50 karakter olabilir'
                                        }
                                    }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Bina No / Daire"
                                            placeholder="Bina numarası giriniz"
                                            error={!!errors.buildingNo}
                                            helperText={errors.buildingNo?.message}
                                            variant="outlined"
                                            inputProps={{ maxLength: 50 }}
                                            sx={fieldStyles}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default CreatePhysicalCardForm;
